/**
 * Turns a pasted list of people into attendee rows.
 *
 * The reason this exists rather than an "add one person" form repeated: the
 * lists administrators actually hold are exports — a Google Form response
 * summary, a partner's guest list, a column copied out of a spreadsheet. Typing
 * forty names into forty forms is where the transcription errors come from, so
 * the paste is the primary input and the per-row fields are for correcting it.
 *
 * Deliberately forgiving about shape and strict about identity: the email is the
 * only field that must be recognisable, because it is what the enrollment keys
 * on. Everything else is optional and simply improves the record.
 *
 * Recognised per line, in order of preference:
 *
 *   Ada Lovelace, ada@example.com, Analytical Ltd, Engineer, 555-0100
 *   Ada Lovelace <ada@example.com>
 *   Ada Lovelace	ada@example.com	Analytical Ltd        (tab-separated)
 *   ada@example.com
 *
 * A line with no email is returned as a problem rather than dropped, so the
 * paste never silently loses somebody.
 */

import {
  normalizeEmailAddress,
  normalizePhoneForSave,
  normalizeSaveText,
} from "./profileInputs";

export type ParsedAttendeeRow = {
  fullName: string;
  email: string;
  company: string;
  jobTitle: string;
  phone: string;
};

export type AttendeeRosterPasteResult = {
  rows: ParsedAttendeeRow[];
  /** Lines that carried no usable email, with their 1-based position in the paste. */
  problems: Array<{ line: number; text: string; reason: "no_email" | "duplicate" }>;
};

const EMAIL_PATTERN = /[^\s,;<>()[\]]+@[^\s,;<>()[\]]+\.[^\s,;<>()[\]]+/;

/** A header row from a spreadsheet copy, which is a label and not a person. */
function looksLikeHeaderRow(fields: string[]) {
  const joined = fields.join(" ").toLowerCase();
  return /\bemail\b/.test(joined) && /\b(name|first|last)\b/.test(joined);
}

function splitFields(line: string) {
  // Tabs win when present: a spreadsheet copy is tab-separated, and its fields
  // routinely contain commas ("Acme, Inc.") that a comma split would break apart.
  const separator = line.includes("\t") ? "\t" : ",";
  return line.split(separator).map(field => field.trim());
}

function stripAngleBrackets(value: string) {
  return value.replace(/[<>]/g, " ").replace(/\s+/g, " ").trim();
}

/** A capitalised guess at a name for a line that carried only an address. */
function nameFromEmail(email: string) {
  const localPart = email.split("@")[0] ?? "";
  const words = localPart
    .split(/[._\-+]+/)
    .map(word => word.trim())
    .filter(word => /[a-z]/i.test(word));
  if (words.length === 0) return localPart;
  return words.map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");
}

export function parseAttendeeRosterPaste(text: string): AttendeeRosterPasteResult {
  const rows: ParsedAttendeeRow[] = [];
  const problems: AttendeeRosterPasteResult["problems"] = [];
  const seen = new Set<string>();

  const lines = String(text ?? "").split(/\r?\n/);
  lines.forEach((rawLine, index) => {
    const line = rawLine.trim();
    if (!line) return;

    const fields = splitFields(line);
    if (looksLikeHeaderRow(fields)) return;

    const emailMatch = EMAIL_PATTERN.exec(line);
    if (!emailMatch) {
      problems.push({ line: index + 1, text: line, reason: "no_email" });
      return;
    }

    const pastedEmail = emailMatch[0];
    const email = normalizeEmailAddress(pastedEmail);
    const emailNormalized = email;
    if (seen.has(emailNormalized)) {
      problems.push({ line: index + 1, text: line, reason: "duplicate" });
      return;
    }
    seen.add(emailNormalized);

    // The remaining fields, in their original order, with the one holding the
    // address removed — so positions stay meaningful whether the email came
    // first, second, or inside angle brackets after the name.
    const withoutEmail = fields
      .map(field => stripAngleBrackets(field.replace(pastedEmail, " ")))
      .map(field => field.trim());

    const named = withoutEmail.map(normalizeSaveText).filter(Boolean);
    const fullName = named[0] ?? nameFromEmail(email);
    const pastedPhone = named[3] ?? "";
    let phone = "";
    try {
      phone = normalizePhoneForSave(pastedPhone) ?? "";
    } catch {
      /*
       * Keep malformed nonblank input visible for correction. Dropping it would
       * turn an invalid phone into "no phone", while passing it on lets the
       * enrollment schema return its explicit phone validation error.
       */
      phone = pastedPhone;
    }

    rows.push({
      fullName: normalizeSaveText(fullName).slice(0, 160),
      email: email.slice(0, 320),
      company: (named[1] ?? "").slice(0, 180),
      jobTitle: (named[2] ?? "").slice(0, 180),
      phone: phone.slice(0, 40),
    });
  });

  return { rows, problems };
}
